// Query-key factories for the customer portal. The active account is a SERVER
// scope: every list read threads it as the `account_id` query param, so the
// active membership rides the key through the params record each factory takes
// — switching accounts produces a different key and refetches.
export const customerKeys = {
  context: () => ["customer", "me"] as const,
  // One card per account: switching accounts reads the other account's card.
  authorizationCode: (accountId: string) =>
    ["customer", "account", accountId, "authorization-code"] as const,
  // One contact read per account; the contact save writes its answer here.
  accountContact: (accountId: string) => ["customer", "account-contact", accountId] as const,
  // Keyed on the raw token because the token IS the identity of the read; the
  // page holds exactly one at a time and it is already in the address bar.
  invitePreview: (token: string) => ["customer", "invite-preview", token] as const,
  // The public branding read and its logo are the same for every reader of a
  // utility's portal, signed in or not, so the slug alone is the key.
  portalBranding: (slug: string) => ["customer", "portal", slug] as const,
  portalLogo: (slug: string) => ["customer", "portal", slug, "logo"] as const,
  facilities: (params: Record<string, unknown>) => ["customer", "facilities", params] as const,
  facility: (id: string) => ["customer", "facilities", id] as const,
  installations: (params: Record<string, unknown>) =>
    ["customer", "installations", params] as const,
  installation: (id: string) => ["customer", "installations", id] as const,
  // Billed devices are server-paged and scoped to ONE account by the path, so
  // the account id sits in the key ahead of the page/sort/filter params — a
  // switch is a different key, and the hook compares that slot to decide
  // whether the previous page may stand in while the next one loads.
  billedInstallations: (accountId: string, params: Record<string, unknown>) =>
    ["customer", "account", accountId, "billed-installations", params] as const,
  inventoryInstallations: (id: string) => ["customer", "inventory", id, "installations"] as const,
  inspections: (params: Record<string, unknown>) => ["customer", "inspections", params] as const,
  submissions: (params: Record<string, unknown>) => ["customer", "submissions", params] as const,
  // History's one server-paged page: params carries the account, the filters,
  // the sort and the window. A key of its own, outside the `submissions`
  // prefix, so the page shape and the drained read's shape never share an
  // entry; the hook reads the params back from slot 2 to compare accounts.
  submissionPage: (params: Record<string, unknown>) =>
    ["customer", "submission-page", params] as const,
  submission: (id: string) => ["customer", "submissions", id] as const,
  // NO templateVersion key: the redacted `definition` rides the
  // getCustomerSubmission detail body (index amendment), so there is no
  // separate template-version read and the renderer reads submission.definition.
  submissionAttachment: (submissionId: string, attachmentId: string) =>
    ["customer", "submissions", submissionId, "attachments", attachmentId] as const,
  violations: (params: Record<string, unknown>) => ["customer", "violations", params] as const,
  violation: (id: string) => ["customer", "violations", id] as const,
  eligibleVendors: (facilityId: string) => ["customer", "eligible-vendors", facilityId] as const,
  // The request list fetches its whole matching set in one page (the derived
  // customer status has no server filter, so the status lens is client-side and
  // must see every row), so `params` carries only the server-side narrowing.
  testRequests: (params: Record<string, unknown>) => ["customer", "test-requests", params] as const,
  // Inside the feed's prefix, so the cancel verb's invalidation reaches the record too.
  testRequest: (id: string) => ["customer", "test-requests", id] as const,
  // One key per thread + message window: the pane pages the embedded messages
  // array, and the optimistic append writes THIS entry.
  messageThread: (threadId: string, params: Record<string, unknown>) =>
    ["customer", "message-threads", threadId, params] as const,
  // Server-paged: params carries the page window and the sort as well as the
  // filters, so each page/sort gets its own cache entry.
  notices: (params: Record<string, unknown>) => ["customer", "notices", params] as const,
  notice: (id: string) => ["customer", "notices", id] as const,
  noticePdf: (id: string) => ["customer", "notices", id, "pdf"] as const,
  reportForms: (submissionId: string) =>
    ["customer", "submissions", submissionId, "report-forms"] as const,
};
